import { useHoverPopover } from '@app/core/hooks';
import { Box, Popover, PopoverOrigin, Typography } from '@mui/material';
import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';

/** Context so descendants can close the popover (e.g. on button click). */
const HoverPopoverCloseContext = createContext<(() => void) | null>(null);

/** Hook for content inside HoverPopover to close it programmatically. */
export function useHoverPopoverClose(): (() => void) | null {
  return useContext(HoverPopoverCloseContext);
}

export interface HoverPopoverProps {
  /** Element that opens the popover on hover. */
  trigger: ReactNode;
  /** Title above the content (subtitle2). */
  title?: ReactNode;
  /** Content below the title. */
  children: ReactNode;
  /** Delay in ms before closing when leaving the trigger. @default 150 */
  delay?: number;
  /** When false, the popover never opens. */
  enabled?: boolean;
  /**
   * Positioning mode.
   * - 'element': anchor to the trigger (default)
   * - 'cursor': at the cursor when opening
   * - 'cursor-x-element-y': X from cursor, Y from trigger top
   */
  positionMode?: 'element' | 'cursor' | 'cursor-x-element-y';
  /** Offset in px for cursor / cursor-x-element-y modes. */
  cursorOffset?: { top?: number; left?: number };
  /** Popover anchor origin. */
  anchorOrigin?: PopoverOrigin;
  /** Popover transform origin. */
  transformOrigin?: PopoverOrigin;
  /** Extra sx for the content Box. */
  contentSx?: Record<string, unknown>;
  /** Extra sx for the Paper. */
  paperSx?: Record<string, unknown>;
  /** Fires when the trigger↔popover hover bridge opens/closes. */
  onHoverChange?: (hovered: boolean) => void;
}

/** Default origins for cursor-based modes: bottom-center at the anchor point. */
const CURSOR_POSITION_DEFAULTS = {
  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'center' as const },
  transformOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'center' as const,
  },
};

const ELEMENT_POSITION_DEFAULTS = {
  anchorOrigin: { vertical: 'top' as const, horizontal: 'right' as const },
  transformOrigin: { vertical: 'bottom' as const, horizontal: 'right' as const },
};

function usesCursorPosition(positionMode: HoverPopoverProps['positionMode']) {
  return positionMode === 'cursor' || positionMode === 'cursor-x-element-y';
}

/**
 * Shows content when hovering the trigger, with a bridge so the cursor can
 * move into the popover without it closing.
 */
export function HoverPopover({
  trigger,
  title,
  children,
  delay = 150,
  enabled = true,
  positionMode = 'element',
  cursorOffset,
  anchorOrigin = usesCursorPosition(positionMode)
    ? CURSOR_POSITION_DEFAULTS.anchorOrigin
    : ELEMENT_POSITION_DEFAULTS.anchorOrigin,
  transformOrigin = usesCursorPosition(positionMode)
    ? CURSOR_POSITION_DEFAULTS.transformOrigin
    : ELEMENT_POSITION_DEFAULTS.transformOrigin,
  contentSx,
  paperSx,
  onHoverChange,
}: HoverPopoverProps) {
  const {
    anchorEl,
    anchorPosition,
    anchorReference,
    menuOpen,
    handleMouseEnter,
    handleMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
    closePopover,
  } = useHoverPopover({ enabled, delay, positionMode, cursorOffset });

  // Keep external highlight in sync with the trigger↔popover hover bridge.
  const onHoverChangeRef = useRef(onHoverChange);
  onHoverChangeRef.current = onHoverChange;
  useEffect(() => {
    onHoverChangeRef.current?.(menuOpen);
  }, [menuOpen]);

  const onPopoverEnter = () => {
    handlePopoverMouseEnter();
    // Re-assert after parents (e.g. lane row) clear on mouseLeave while moving into the popover.
    onHoverChangeRef.current?.(true);
  };

  return (
    <>
      <Box
        component="span"
        sx={{ display: 'inline-flex' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {trigger}
      </Box>
      <Popover
        open={menuOpen}
        anchorEl={anchorEl}
        anchorReference={anchorReference}
        anchorPosition={anchorPosition}
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            onMouseEnter: onPopoverEnter,
            onMouseLeave: handlePopoverMouseLeave,
            sx: { pointerEvents: 'auto', ...paperSx },
          },
        }}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableRestoreFocus
      >
        <HoverPopoverCloseContext.Provider value={closePopover}>
          <Box sx={{ p: 1.5, maxWidth: 280, ...contentSx }}>
            {title && (
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                {title}
              </Typography>
            )}
            {children}
          </Box>
        </HoverPopoverCloseContext.Provider>
      </Popover>
    </>
  );
}
